import React, { useState, useEffect, useMemo } from 'react';

function StatsOverview({ stats, displayMode = 'dollars', unitSize = 25, bets = [], getUnitSizeForDate, advancedUnitSettings = false }) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    // Calculate unit-based totals when in advanced mode
    const unitStats = useMemo(() => {
        if (displayMode !== 'units' || !advancedUnitSettings || !getUnitSizeForDate) {
            return null;
        }

        let totalPLUnits = 0;
        let totalStakedUnits = 0;
        let totalReturnedUnits = 0;

        bets.forEach(bet => {
            const betUnitSize = getUnitSizeForDate(bet.date);
            totalPLUnits += bet.pl / betUnitSize;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                totalStakedUnits += bet.stake / betUnitSize;
            }
            
            // Only count returned for settled bets (not pending)
            if (bet.result !== 'pending') {
                totalReturnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
        });

        return {
            totalPL: totalPLUnits,
            totalStaked: totalStakedUnits,
            totalReturned: totalReturnedUnits
        };
    }, [displayMode, advancedUnitSettings, bets, getUnitSizeForDate]);

    const formatValue = (value, showPrefix = true) => {
        if (displayMode === 'units') {
            // Use pre-calculated unit stats if in advanced mode
            const units = advancedUnitSettings && unitStats ? value : value / unitSize;
            const prefix = showPrefix ? (units >= 0 ? '+' : '') : '';
            return `${prefix}${units.toFixed(1)}U`;
        }
        const prefix = showPrefix ? (value >= 0 ? '+' : '') : '';
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatStaked = (value) => {
        if (displayMode === 'units') {
            const units = advancedUnitSettings && unitStats ? value : value / unitSize;
            return `${units.toFixed(1)}U`;
        }
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    };

    // Get display values based on mode
    const displayTotalPL = displayMode === 'units' && advancedUnitSettings && unitStats ? unitStats.totalPL : stats.totalPL;
    const displayTotalStaked = displayMode === 'units' && advancedUnitSettings && unitStats ? unitStats.totalStaked : stats.totalStaked;
    const displayTotalReturned = displayMode === 'units' && advancedUnitSettings && unitStats ? unitStats.totalReturned : stats.totalReturned;

    const formatPercentage = (value) => {
        const prefix = value >= 0 ? '+' : '';
        return `${prefix}${value}%`;
    };

    const statCards = [
        {
            label: 'Total P/L',
            value: formatValue(displayTotalPL),
            subValue: `ROI: ${formatPercentage(stats.roi)}`,
            isPositive: displayTotalPL >= 0,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            highlight: true
        },
        {
            label: 'Win Rate',
            value: `${stats.winRate}%`,
            subValue: `${stats.wins}W - ${stats.losses}L`,
            isPositive: stats.winRate >= 50,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        },
        {
            label: 'Total Bets',
            value: stats.totalBets.toLocaleString(),
            subValue: `${stats.cashedOut || 0} cashed out${stats.pending ? ` • ${stats.pending} pending` : ''}`,
            isNeutral: true,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
            )
        },
        {
            label: 'Avg Odds',
            value: stats.avgOdds.toFixed(2),
            subValue: `Win Avg: ${stats.avgWinOdds}`,
            isNeutral: true,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
            )
        },
        {
            label: 'Total Staked',
            value: formatStaked(displayTotalStaked),
            subValue: `Returned: ${formatStaked(displayTotalReturned)}`,
            isNeutral: true,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            )
        }
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4 mb-4 md:mb-6">
            {statCards.map((stat, index) => (
                <div 
                    key={index}
                    className={`relative overflow-hidden rounded-xl md:rounded-2xl border transition-all duration-500 hover:scale-[1.02] hover:shadow-lg ${
                        stat.highlight 
                            ? 'bg-gradient-to-br from-emerald-900/40 to-teal-900/40 border-emerald-500/30 hover:shadow-emerald-500/10'
                            : 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600/50 hover:shadow-slate-500/5'
                    } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                >
                    {/* Subtle gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
                    
                    <div className="relative p-3 md:p-4">
                        {/* Icon and Label */}
                        <div className="flex items-center gap-2 mb-2 md:mb-3">
                            <div className={`p-1.5 md:p-2 rounded-lg ${
                                stat.highlight 
                                    ? 'bg-emerald-500/20 text-emerald-400'
                                    : 'bg-slate-800/50 text-slate-400'
                            }`}>
                                <div className="w-4 h-4 md:w-5 md:h-5 [&>svg]:w-full [&>svg]:h-full">
                                    {stat.icon}
                                </div>
                            </div>
                            <span className="text-xs md:text-xs font-medium text-slate-400 uppercase tracking-wider truncate">
                                {stat.label}
                            </span>
                        </div>

                        {/* Main Value */}
                        <div className={`text-xl md:text-2xl font-bold mb-0.5 md:mb-1 truncate ${
                            stat.isNeutral 
                                ? 'text-white'
                                : stat.isPositive 
                                    ? 'text-emerald-400'
                                    : stat.isNegative
                                        ? 'text-red-400'
                                        : stats.totalPL >= 0 && stat.highlight
                                            ? 'text-emerald-400'
                                            : stat.highlight
                                                ? 'text-red-400'
                                                : 'text-white'
                        }`}>
                            {stat.value}
                        </div>

                        {/* Sub Value */}
                        <div className="text-xs md:text-xs text-slate-500 truncate">
                            {stat.subValue}
                        </div>
                    </div>

                    {/* Bottom accent line for highlight card */}
                    {stat.highlight && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500" />
                    )}
                </div>
            ))}
        </div>
    );
}

export default StatsOverview;
